/* eslint-disable @typescript-eslint/naming-convention */
import { isUserLoggedIn } from "utils";
import { getCandidateFeed } from "utils/apiClientPrivate";
import { getPublicJobFeed } from "utils/apiClients";
import { JOBS_PER_PAGE, moduleJobs } from "utils/constants";
import { create } from "zustand";
import departmentData from "cms/department-data";

import { getKeyByValue, SALARY_SCALE } from "constants/filters";
import { jobsListTransformer } from "utils/jobFeedApiParser";
import { Correction } from "types/job/jobCorrectionType";
import { transformFilterObject } from "utils/helpers";
import Axios from "axios";
import {
	MIN_EXPERIENCE_DEFAULT_VALUE,
	POSTED_IN_DEFAULT_VALUE,
	VALUE_TO_LABEL_MAPPING,
} from "../constants";

export const MULTI_SELECT_FILTERS = [
	"department_id",
	"work_type",
	"work_mode",
	"work_shift",
];

let latestRequestId = 0;
const cancelTokenMap = new Map<number, any>(); // Map of requestId → cancelToken

interface StoreState {
	jobsInStore: { type: string | number }[];
	jobsCount: number;
	totalJobsPages: number | null;
	filters: {
		department_id: number[];
		salary: string;
		min_experience: number;
		work_mode: string[];
		work_type: string[];
		work_shift: string[];
		posted_in: number;
		sort_by: string;
		page: number;
		page_size: number;
		education_level: string;
		gender: string;
		english_level: string;
		distance: string;
		featured_jobs: boolean;
	};
	urlDataObject: {
		slugEntityDetails: {};
		path: null;
		searchQuery: {};
	};
	areJobsLoading: boolean;
	page_size: number;
	citySearchObject: {};
	titleSearchObject: {};
	experienceSearchObject: {};
	isDesktopSearchExpanded: {};
	appliedFiltersCount: number;
	appliedFilters: {
		filterType: string;
		filterValue: string;
		filterLabel: string;
	}[];
	resetFiltersLocalState: boolean;
	defaultLocationCleared: boolean;
	jobFeedSearchInputPrefillData: {};
	// For mixpanel events to check if inputs were manually selected or pre-filled
	manualUserSearchInputs: {
		location: string | null;
		keyword: string | null;
		experience: string | null;
	};
	searchCorrectionObject: Correction;
	searchSessionId: string;
	discoveryTabs: {
		showTabs: boolean;
		activeTabId: number;
		data: {
			id: number;
			appliedFilters: {};
			value: { [key: string]: string | number };
		}[];
	};
	setFiltersInitialState: (preAppliedFilters) => void;
	setJobs: (jobsData, totalJobCount) => void;
	fetchJobs: () => void;
	applyFilter: (category, value) => void;
	setUrlSearchQuery: (queryObject) => void;
	setSlugEntityDetails: (slugEntity) => void;
	setUrlPath: (slug) => void;
	resetFilters: () => void;
	setCitySearchObject: (obj) => void;
	setTitleSearchObject: (obj) => void;
	setExperienceSearchObject: (obj) => void;
	setIsDesktopSearchExpanded: (value) => void;
	resetSearch: () => void;
	updateAppliedFiltersCount: () => void;
	clearAllFilters: () => void;
	setDefaultLocationCleared: (bool) => void;
	setJobFeedSearchInputPrefillData: (data) => void;
	setManualUserSearchInputs: (inputObject: {
		location?: string;
		keyword?: string;
		experience?: string;
	}) => void;
	applyMobileFilters: (mobileFiltersObject) => void;
	addExperienceInFilters: (value) => void;
	setSearchCorrectionObject: (obj) => void;
	setSearchSessionId: (obj) => void;
	setShowDiscoveryTabs: (value) => void;
	setActiveDiscoveryTab: (value) => void;
	clearAppliedFiltersAndApplyTab: () => void;
	moduleJobs: {}[];
	updateModuleJob: (value, obj) => void;
	fetchModuleJobs: (value) => void;
}

const filtersInitialState = {
	department_id: [],
	salary: "",
	min_experience: MIN_EXPERIENCE_DEFAULT_VALUE,
	work_mode: [],
	work_type: [],
	work_shift: [],
	posted_in: -1,
	sort_by: "",
	page: 1,
	page_size: JOBS_PER_PAGE,
	education_level: "",
	gender: "",
	english_level: "",
	distance: "",
	featured_jobs: false,
};

const discoveryTabsInitialState = {
	showTabs: false,
	activeTabId: 1,
	data: [
		{
			id: 1,
			appliedFilters: {},
			value: {},
		},
		{
			id: 2,
			appliedFilters: {},
			value: {
				sort_by: "-max_salary",
			},
		},
		{
			id: 3,
			appliedFilters: {},
			value: {
				distance: "lt-10",
			},
		},
		{
			id: 4,
			appliedFilters: {},
			value: {
				posted_in: 72,
			},
		},
	],
};

const urlDataObjectInitialState = {
	slugEntityDetails: {},
	path: null,
	searchQuery: {},
};

const useJobFeedStore = create<StoreState>((set, get) => ({
	jobsInStore: [],
	moduleJobs: [],
	jobsCount: 0,
	totalJobsPages: null,
	filters: filtersInitialState,
	urlDataObject: urlDataObjectInitialState,
	page_size: JOBS_PER_PAGE,
	areJobsLoading: false,
	citySearchObject: {},
	titleSearchObject: {},
	experienceSearchObject: {},
	isDesktopSearchExpanded: false,
	appliedFilters: [],
	appliedFiltersCount: 0,
	resetFiltersLocalState: false,
	defaultLocationCleared: false,
	jobFeedSearchInputPrefillData: {},
	manualUserSearchInputs: {
		keyword: null,
		location: null,
		experience: null,
	},
	searchCorrectionObject: {} as Correction,
	searchSessionId: "",
	discoveryTabs: discoveryTabsInitialState,
	setActiveDiscoveryTab: (tabId: number) => {
		const prevActiveTabID = get().discoveryTabs.activeTabId;

		const updatedData = get().discoveryTabs.data.map((tab) =>
			tab.id === prevActiveTabID
				? { ...tab, appliedFilters: { ...get().filters } } // persist appliedFilters on the previously active tab
				: tab
		);

		get().resetFilters();

		set((state) => {
			const selectedTab = state.discoveryTabs.data.find((tab) => tab.id === tabId);

			const transformedTabsAppliedFilters = transformFilterObject({
				...selectedTab.appliedFilters,
			});

			return {
				discoveryTabs: {
					...state.discoveryTabs,
					activeTabId: tabId,
					data: updatedData, // persist the previous tab's appliedFilters
				},
				appliedFilters: transformedTabsAppliedFilters, // Merge previously applied filters from the selected tab
				filters: {
					...filtersInitialState, // Reset filters
					...selectedTab.appliedFilters, // Merge previously applied filters from the selected tab
				},
			};
		});

		get().fetchJobs();
		get().updateAppliedFiltersCount();
	},
	setShowDiscoveryTabs: (show: boolean) => {
		set((state) => ({
			discoveryTabs: {
				...state.discoveryTabs,
				showTabs: show,
			},
		}));
	},
	// clear additional filters and apply active tab's
	clearAppliedFiltersAndApplyTab: () => {
		const { activeTabId, showTabs } = get().discoveryTabs;

		if (!activeTabId || !showTabs) return;
		set((state) => {
			const updatedData = state.discoveryTabs.data.map((tab) =>
				tab.id === state.discoveryTabs.activeTabId
					? {
							...tab,
							appliedFilters: {},
					  }
					: tab
			);

			return {
				discoveryTabs: {
					...state.discoveryTabs,
					data: updatedData,
				},
				filters: {
					...filtersInitialState, // Reset filters
				},
			};
		});
	},
	setFiltersInitialState: (preAppliedFilters) => {
		if (Array.isArray(preAppliedFilters) && preAppliedFilters?.length > 0) {
			preAppliedFilters.forEach((filter) => {
				let filterObj = {
					filterLabel:
						VALUE_TO_LABEL_MAPPING[filter?.name]?.[filter?.selected_value] ||
						filter?.selected_value,
					filterType: filter?.name,
					filterValue: filter?.selected_value,
				};

				if (filter.name === "salary") {
					filterObj = {
						filterLabel:
							VALUE_TO_LABEL_MAPPING[filter?.name]?.[
								filter?.selected_value
							] || filter?.selected_value,
						filterType: filter?.name,
						filterValue: getKeyByValue(
							SALARY_SCALE,
							Number(filter?.selected_value)
						),
					};
				}
				if (filter.name === "department_id") {
					const dept = departmentData?.find(
						(d) => d.id === filter.selected_value[0]
					);
					filterObj = {
						filterLabel: dept?.name,
						filterType: filter.name,
						filterValue: filter.selected_value[0],
					};
				}
				if (filter.name === "featured_jobs") {
					filterObj = {
						filterLabel: "Featured jobs",
						filterType: filter?.name,
						filterValue: true,
					};
				}

				function addFilterIfNotPresent(appliedFilters, newFilter) {
					const isPresent = appliedFilters.some(
						(f) => f.filterType === newFilter.filterType
					);

					if (!isPresent) {
						return [...appliedFilters, filterObj];
					}
					return appliedFilters;
				}

				set((state) => ({
					...state,
					filters: {
						...get().filters,
						[filter?.name]: filter?.selected_value,
					},
					appliedFilters: addFilterIfNotPresent(
						get().appliedFilters,
						filterObj
					),
				}));
			});
		}
		// If experience input in search exists, it will already be present in filtersAppliedState.
		// Following block of code should not run in that case.
		if (
			get().experienceSearchObject?.display_text &&
			!get().appliedFilters.find((filter) => filter.filterType === "min_experience")
		) {
			set((state) => ({
				...state,
				filters: {
					...get().filters,
					min_experience:
						get().experienceSearchObject?.experience_input ||
						MIN_EXPERIENCE_DEFAULT_VALUE,
				},
				appliedFilters: [
					...get().appliedFilters,
					{
						filterLabel:
							get().experienceSearchObject?.display_text ===
							"I am a Fresher"
								? "Fresher"
								: get().experienceSearchObject?.display_text,
						filterType: "min_experience",
						filterValue: get().experienceSearchObject?.experience_input,
					},
				],
			}));
		}
		get().updateAppliedFiltersCount();
	},
	setJobs: (jobsData, totalJobCount) => {
		set((state) => ({
			...state,
			jobsInStore: jobsData || [],
			jobsCount: totalJobCount,
			totalJobsPages: Math.ceil(totalJobCount / JOBS_PER_PAGE),
		}));
	},
	updateModuleJob: (moduleKey, updatedJobData) =>
		set((state) => ({
			moduleJobs: {
				...state.moduleJobs,
				[moduleKey]: {
					...(state.moduleJobs[moduleKey] || {}),
					...updatedJobData,
				},
			},
		})),
	fetchModuleJobs: async (element_id) => {
		const { urlDataObject } = get();
		const { searchQuery } = urlDataObject;
		const query = {
			element_id,
			c_id: searchQuery?.c_id,
			page_size: 20,
			page: 1,
		};
		try {
			const response = await getCandidateFeed(query);
			get().updateModuleJob(element_id, response?.data?.results?.job_feed);
		} catch (error) {
			console.error("Failed to fetch jobs:", error);
		}
	},
	fetchJobs: async () => {
		latestRequestId += 1;
		const requestId = latestRequestId;
		// Cancel previous request if it exists
		if (cancelTokenMap.has(requestId - 1)) {
			const prevTokenSource = cancelTokenMap.get(requestId - 1);
			if (prevTokenSource) {
				prevTokenSource.cancel("Request cancelled due to new request");
			}
			cancelTokenMap.delete(requestId - 1);
		}

		const cancelTokenSource = Axios.CancelToken.source();
		cancelTokenMap.set(requestId, cancelTokenSource);

		set((state) => ({ ...state, areJobsLoading: true }));
		let query = {};
		const { filters, urlDataObject, searchCorrectionObject, discoveryTabs } = get();
		const { path, searchQuery } = urlDataObject;
		let newQueryObj = searchQuery;
		if (!filters.featured_jobs) {
			newQueryObj = Object.fromEntries(
				Object.entries(get().urlDataObject.searchQuery).filter(
					([key, value]) => !(key === "featured_jobs" && value === "true")
				)
			);

			get().setUrlSearchQuery(newQueryObj);
		}

		// Convert search query, filters, and page into a one object
		Object.keys(newQueryObj).forEach((key) => {
			query[key] = searchQuery[key];
		});

		// for implicit correction utilize the suggestions on applying filters.
		if (
			searchCorrectionObject &&
			Object.keys(searchCorrectionObject)?.length > 0 &&
			searchCorrectionObject?.is_implicit
		) {
			query = {
				...query,
				entity_id: searchCorrectionObject?.suggestion?.entity_id,
				entity_type: searchCorrectionObject?.suggestion?.entity_type,
				raw_text_correction: false,
				text: searchCorrectionObject?.corrected_text,
			};
		}

		// Pass selected tab and tab value in payload
		// We are appending tab's filters first into the query before applying all the filters to
		// give high precedence to user's applied filters over the tab filters.

		const { showTabs, activeTabId } = discoveryTabs;
		if (showTabs && activeTabId) {
			const selectedTab = discoveryTabs.data.find((tab) => tab.id === activeTabId);
			query = {
				...query,
				...selectedTab.value,
				selected_tab: discoveryTabs?.activeTabId,
			};
		}

		Object.keys(filters).forEach((key) => {
			if (Array.isArray(filters[key]) && filters[key].length > 0) {
				query[key] = filters[key].join(",");
			} else if (
				filters[key] &&
				filters[key] !== -1 &&
				filters[key] !== MIN_EXPERIENCE_DEFAULT_VALUE
			) {
				query[key] = filters[key];
			}
		});

		if (path)
			Object.keys(path).forEach((key) => {
				query[key] = path[key];
			});
		// Make an API call with above object for jobs to user-profile-orchestrator
		if (window) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
		try {
			let response;
			if (!isUserLoggedIn()) {
				response = await getPublicJobFeed(query, cancelTokenSource.token);
			} else {
				response = await getCandidateFeed(query, false, cancelTokenSource.token);
			}

			if (requestId !== latestRequestId) return;

			const { jobsList, pagination } = jobsListTransformer(response?.data?.results);
			const jobCount = response?.data?.count || Number(pagination?.total_size) || 0;

			// Extract jobs and count from response and set in store
			set((state) => ({
				...state,
				jobsCount: jobCount,
				jobsInStore: jobsList,
				totalJobsPages:
					Math.ceil(Number(response?.data?.count) / JOBS_PER_PAGE) ||
					Number(pagination?.total_pages),
				areJobsLoading: false,
			}));

			// // Update URL with final query params
			// const params = new URLSearchParams();
			// // Construct the new URL
			// Object.keys(query).forEach((key) => {
			// 	const value = query[key];
			// 	// Check if the value is valid (e.g., not null, undefined, or empty)
			// 	if (
			// 		value !== null &&
			// 		value !== undefined &&
			// 		value !== "" &&
			// 		!(Array.isArray(value) && value.length === 0) &&
			// 		value !== MIN_EXPERIENCE_DEFAULT_VALUE
			// 	) {
			// 		params.set(key, value);
			// 	}
			// });
			// const newUrl = `${window.location.pathname}?${params.toString()}`;
			// // Replace the current URL without reloading or re-rendering
			// window.history.pushState({}, "", newUrl);
		} catch (e) {
			console.error("error fetching jobs", e);

			if (requestId === latestRequestId) {
				set((state) => ({ ...state, areJobsLoading: false }));
			}
		} finally {
			cancelTokenMap.delete(requestId);
		}
	},
	applyFilter: (category, value) => {
		const { filters } = get();

		// Check filter type - Multi select
		if (MULTI_SELECT_FILTERS.includes(category)) {
			if (filters[category].includes(value)) {
				const indexOfValue = filters[category].findIndex(
					(item) => item === value
				);
				const updatedValuesArray = filters[category].toSpliced(indexOfValue, 1);
				// Update the filter state with new array which does not contain the removed filter
				set((state) => ({
					...state,
					filters: {
						...filters,
						[category]: updatedValuesArray,
						page: 1,
					},
				}));

				// Remove the filter from applied filters array
				const indexOfFilterInAppliedFilters = get().appliedFilters.findIndex(
					(filter) =>
						filter.filterType === category && filter.filterValue === value
				);
				const updatedAppliedFiltersArray = get().appliedFilters.toSpliced(
					indexOfFilterInAppliedFilters,
					1
				);
				set((state) => ({
					...state,
					appliedFilters: updatedAppliedFiltersArray,
				}));
			} else {
				// Update filters state with selected filter
				set((state) => ({
					...state,
					filters: {
						...filters,
						[category]: [...filters[category], value],
						page: 1,
					},
				}));

				// Update Applied filters state with selected filter
				if (category === "department_id" && Array.isArray(departmentData)) {
					const dept = departmentData.find((d) => d.id === value);
					set((state) => ({
						...state,
						appliedFilters: [
							{
								filterLabel: dept?.name,
								filterType: "department_id",
								filterValue: value,
							},
							...get().appliedFilters,
						],
					}));
				} else {
					set((state) => ({
						...state,
						appliedFilters: [
							{
								filterLabel:
									VALUE_TO_LABEL_MAPPING[category]?.[value] || value,
								filterType: category,
								filterValue: value,
							},
							...get().appliedFilters,
						],
					}));
				}
			}
		} else {
			// Update single select filters
			set((state) => ({
				...state,
				filters: {
					...filters,
					page: 1,
					[category]: value,
				},
			}));

			// Remove or add to appliedFilters
			const existingFilterIndex = get().appliedFilters.findIndex(
				(filter) => filter.filterType === category
			);

			// Handle featured_jobs specifically
			if (category === "featured_jobs") {
				// If the value is false, remove it from appliedFilters
				if (value === false) {
					if (existingFilterIndex !== -1) {
						const updatedAppliedFiltersArray = get().appliedFilters.toSpliced(
							existingFilterIndex,
							1
						);
						set((state) => ({
							...state,
							appliedFilters: updatedAppliedFiltersArray,
						}));
					}
				} else {
					// If it's true, add it back to appliedFilters
					let updatedAppliedFiltersArray = get().appliedFilters;
					if (existingFilterIndex !== -1) {
						updatedAppliedFiltersArray = get().appliedFilters.toSpliced(
							existingFilterIndex,
							1
						);
					}

					updatedAppliedFiltersArray = [
						{
							filterLabel: "Featured jobs", // You can use VALUE_TO_LABEL_MAPPING if needed
							filterType: category,
							filterValue: true,
						},
						...updatedAppliedFiltersArray,
					];

					set((state) => ({
						...state,
						appliedFilters: updatedAppliedFiltersArray,
					}));
				}
			} else if (existingFilterIndex !== -1) {
				// If category exists, remove it from appliedFilters
				let updatedAppliedFiltersArray = get().appliedFilters.toSpliced(
					existingFilterIndex,
					1
				);

				if (
					value !== "" &&
					value !== MIN_EXPERIENCE_DEFAULT_VALUE &&
					value !== 0 &&
					value !== POSTED_IN_DEFAULT_VALUE
				) {
					updatedAppliedFiltersArray = [
						{
							filterLabel:
								VALUE_TO_LABEL_MAPPING[category]?.[value] || value,
							filterType: category,
							filterValue: value,
						},
						...updatedAppliedFiltersArray,
					];
				}

				set((state) => ({
					...state,
					appliedFilters: updatedAppliedFiltersArray,
				}));
			} else {
				// Handle new filter additions
				const shouldSkipFilter = (value, category) => {
					const skipConditions = {
						sort_by: value === "",
						min_experience: value === MIN_EXPERIENCE_DEFAULT_VALUE,
						salary: value === 0,
						posted_in: value === -1,
						distance: value === "",
					};
					return skipConditions[category] ?? false;
				};

				if (shouldSkipFilter(value, category)) return;

				const newFilter = {
					filterLabel: VALUE_TO_LABEL_MAPPING[category]?.[value] || value,
					filterType: category,
					filterValue: value,
				};

				// Apply the filter unless it's the "page" category
				if (category !== "page") {
					set((state) => ({
						...state,
						appliedFilters: [newFilter, ...get().appliedFilters],
					}));
				}
			}
		}

		// Make an API call with updated filter object
		get().fetchJobs();
		get().updateAppliedFiltersCount();
	},

	applyMobileFilters: (mobileFiltersObject) => {
		set((state) => ({
			...state,
			filters: { ...mobileFiltersObject, page: 1 },
		}));
		get().fetchJobs();
		get().updateAppliedFiltersCount();
	},
	setUrlSearchQuery: (queryObject) => {
		set((state) => ({
			...state,
			urlDataObject: { ...get().urlDataObject, searchQuery: queryObject },
		}));
	},
	setSlugEntityDetails: (slugEntity) => {
		set((state) => ({
			...state,
			urlDataObject: { ...get().urlDataObject, slugEntityDetails: slugEntity },
			citySearchObject:
				slugEntity?.type === "city" ? slugEntity : get().citySearchObject,
		}));
	},
	setUrlPath: (slug) => {
		set((state) => ({
			...state,
			urlDataObject: { ...get().urlDataObject, path: slug },
			filters: { ...get().filters, ...slug },
		}));
	},
	setCitySearchObject: (obj) => {
		set((state) => ({ ...state, citySearchObject: obj }));
	},
	setTitleSearchObject: (obj) => {
		set((state) => ({ ...state, titleSearchObject: obj }));
	},
	setExperienceSearchObject: (obj) => {
		set((state) => ({
			...state,
			experienceSearchObject: obj,
		}));
	},
	setIsDesktopSearchExpanded: (value) => {
		set((state) => ({ ...state, isDesktopSearchExpanded: value }));
	},
	resetSearch: () => {
		set((state) => ({
			...state,
			citySearchObject: {},
			titleSearchObject: {},
			experienceSearchObject: {},
			isDesktopSearchExpanded: false,
			jobFeedSearchInputPrefillData: {},

			filters: filtersInitialState,
			appliedFiltersCount: 0,
			appliedFilters: [],
			resetFiltersLocalState: !get().resetFiltersLocalState,
			urlDataObject: {
				...get().urlDataObject,
				searchQuery: {
					...get().urlDataObject?.searchQuery,
					min_experience: null,
				},
				path: null,
			},
		}));
	},
	updateAppliedFiltersCount: () => {
		const { filters } = get();
		let filtersCount = 0;
		Object.keys(filters).forEach((key) => {
			if (Array.isArray(filters[key]) && filters[key].length > 0) {
				filtersCount += filters[key].length;
			} else if (
				!Array.isArray(filters[key]) &&
				filters[key] &&
				key !== "page" &&
				key !== "page_size" &&
				filters[key] !== MIN_EXPERIENCE_DEFAULT_VALUE &&
				filters[key] !== -1
			) {
				filtersCount += 1;
			}
		});
		if (get().urlDataObject?.path) {
			filtersCount -= 1;
		}
		set((state) => ({ ...state, appliedFiltersCount: filtersCount }));
	},
	clearAllFilters: () => {
		get().resetFilters();
		get().clearAppliedFiltersAndApplyTab();
		get().fetchJobs();
	},
	resetFilters: () => {
		set((state) => ({
			...state,
			filters: filtersInitialState,
			appliedFiltersCount: 0,
			appliedFilters: [],
			resetFiltersLocalState: !get().resetFiltersLocalState,
			experienceSearchObject: {},
			urlDataObject: {
				...get().urlDataObject,
				path: null,
				searchQuery: {
					...get().urlDataObject?.searchQuery,
					min_experience: null,
				},
				// path: null,
			},
		}));
	},
	setDefaultLocationCleared: (bool) => {
		set((state) => ({
			...state,
			defaultLocationCleared: bool,
		}));
	},
	setJobFeedSearchInputPrefillData: (data) => {
		set((state) => ({
			...state,
			jobFeedSearchInputPrefillData: data,
		}));
	},
	setManualUserSearchInputs: (inputObject) => {
		set((state) => ({
			...state,
			manualUserSearchInputs: { ...get().manualUserSearchInputs, ...inputObject },
		}));
	},
	/* 
		On MOBILE, on search cta click,
		we want to add the experience value to filters sidebar as well.
	 */
	addExperienceInFilters: (value) => {
		set((state) => ({
			...state,
			filters: { ...get().filters, min_experience: value },
		}));
	},
	setSearchCorrectionObject: (correction) => {
		set((state) => ({
			...state,
			searchCorrectionObject: correction,
		}));
	},
	setSearchSessionId(searchId) {
		set((state) => ({
			...state,
			searchSessionId: searchId,
		}));
	},
}));
export default useJobFeedStore;
